import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthLayout } from '@/features/auth/components/AuthLayout';
import {
  invitationsEmployeur,
  libelleDuRole,
  type InvitationLue,
} from '@/services/invitationsEmployeur.service';
import { useAuthStore } from '@/store/auth.store';

type Etat =
  | { phase: 'chargement' }
  | { phase: 'lue'; invitation: InvitationLue }
  | { phase: 'acceptee'; role: string }
  | { phase: 'refus'; message: string };

/**
 * L'écran que voit la personne INVITÉE, en arrivant depuis son e-mail.
 *
 * C'est la première page du produit qu'elle verra jamais — et elle la voit
 * avant d'avoir un compte ouvert dans cet espace. Elle est donc posée dans
 * `AuthLayout`, comme la connexion : la marque du cabinet y est déjà appliquée
 * (chantier C2), et l'invitée reconnaît le nom de celui qui l'a invitée plutôt
 * qu'une plateforme dont elle n'a jamais entendu parler.
 *
 * Ce que l'écran dit, et ce qu'il ne dit pas
 * ------------------------------------------
 * Il annonce le rôle proposé et l'adresse à laquelle l'invitation a été
 * envoyée. Il ne nomme PAS l'entreprise : l'API ne la rend pas, précisément
 * pour qu'un lien égaré ne renseigne personne.
 *
 * Une seule décision par écran (règle 7) : accepter. Si la personne n'est pas
 * connectée, on l'envoie se connecter et on la ramène ici — le lien reste dans
 * l'adresse, il n'y a rien à retenir.
 */
export function AccepterInvitationPage() {
  const { jeton } = useParams<{ jeton: string }>();
  const naviguer = useNavigate();
  const estConnecte = useAuthStore(etatAuth => etatAuth.isAuthenticated);
  const [etat, setEtat] = useState<Etat>({ phase: 'chargement' });
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    if (!jeton) return;
    void (async () => {
      try {
        setEtat({
          phase: 'lue',
          invitation: await invitationsEmployeur.lireParJeton(jeton),
        });
      } catch (erreur: unknown) {
        setEtat({
          phase: 'refus',
          message:
            (erreur as { response?: { data?: { message?: string } } })?.response
              ?.data?.message ?? "Cette invitation n'est plus valable.",
        });
      }
    })();
  }, [jeton]);

  const accepter = async () => {
    if (!jeton) return;
    if (!estConnecte) {
      // On garde le lien : après connexion, la personne revient ici.
      naviguer(`/login?retour=${encodeURIComponent(`/invitation/${jeton}`)}`);
      return;
    }
    setEnCours(true);
    try {
      const { role } = await invitationsEmployeur.accepter(jeton);
      setEtat({ phase: 'acceptee', role });
    } catch (erreur: unknown) {
      setEtat({
        phase: 'refus',
        message:
          (erreur as { response?: { data?: { message?: string } } })?.response
            ?.data?.message ?? "Cette invitation n'a pas pu être acceptée.",
      });
    } finally {
      setEnCours(false);
    }
  };

  return (
    <AuthLayout
      title="Invitation"
      subtitle="Vous avez été invité à rejoindre un espace"
    >
      {etat.phase === 'chargement' && (
        <p className="text-sm text-neutral-600">Vérification du lien…</p>
      )}

      {etat.phase === 'refus' && (
        <div>
          <p className="text-sm text-neutral-900">{etat.message}</p>
          <p className="mt-4 text-sm text-neutral-500">
            Demandez un nouveau lien à la personne qui vous a invité.
          </p>
        </div>
      )}

      {etat.phase === 'lue' && (
        <div>
          <dl className="text-sm">
            <div className="flex items-baseline justify-between gap-4 py-2">
              <dt className="text-neutral-500">Rôle proposé</dt>
              <dd className="font-medium text-neutral-900">
                {libelleDuRole(etat.invitation.role)}
              </dd>
            </div>
            <div className="flex items-baseline justify-between gap-4 py-2">
              <dt className="text-neutral-500">Adresse invitée</dt>
              <dd className="truncate font-medium text-neutral-900">
                {etat.invitation.email}
              </dd>
            </div>
          </dl>

          <button
            type="button"
            onClick={() => void accepter()}
            disabled={enCours}
            className="mt-6 flex w-full justify-center rounded-md px-4 py-2 text-sm font-medium shadow-sm disabled:opacity-50"
            style={{
              backgroundColor: 'var(--marque-accent)',
              color: 'var(--marque-sur-accent)',
            }}
          >
            {enCours
              ? 'Acceptation…'
              : estConnecte
                ? "Accepter l'invitation"
                : 'Se connecter pour accepter'}
          </button>

          <p className="mt-4 text-xs text-neutral-500">
            Ce lien ne fonctionne qu&apos;avec l&apos;adresse ci-dessus, et
            expire le{' '}
            {new Date(etat.invitation.expireLe).toLocaleDateString('fr-FR')}.
          </p>
        </div>
      )}

      {etat.phase === 'acceptee' && (
        <div>
          <p className="text-sm text-neutral-900">
            Vous accédez désormais à cet espace en tant que{' '}
            <strong>{libelleDuRole(etat.role)}</strong>.
          </p>
          <button
            type="button"
            onClick={() => naviguer('/')}
            className="mt-6 flex w-full justify-center rounded-md px-4 py-2 text-sm font-medium shadow-sm"
            style={{
              backgroundColor: 'var(--marque-accent)',
              color: 'var(--marque-sur-accent)',
            }}
          >
            Entrer
          </button>
        </div>
      )}
    </AuthLayout>
  );
}
